import BaseDialog from "./BaseDialog";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { filterObjBasedOnSearch, SearchBar } from "../SearchBar";
import { ModelPickerTable } from "./ModelPickerTable";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { sortByProperty } from "@/utils/generic";
import {
  IModelExtended,
  ModelPickerLayout,
  PresetSelector,
} from "@/components/Form/ModelSelectField";
import { ModelPickerFilters } from "../Spotflow/ModelPicker/ModelPickerFilters";
import { SpotToast } from "../SpotToast";
import {
  Sparkles,
  ExternalLink,
  MousePointerClick,
  SearchX,
  X,
} from "lucide-react";

import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import { NoxusAlert } from "../UI/NoxusAlert";
import { Button } from "../shadcn/button";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Badge } from "../shadcn/badge";

interface IModelModal {
  isOpen: boolean;
  onClose: () => void;
  models: IModelExtended[];
  modelType: string;
  selectedModels: IModelExtended[];
  setSelectedModels: Dispatch<SetStateAction<IModelExtended[]>>;
  customValue: string[];
  minModels?: number;
  maxModels?: number;
  layout?: ModelPickerLayout;
  allModelsData?: IModelExtended[];
  showPresets?: boolean;
  hideDisabledModels?: boolean;
  /** Provider TYPE → logo URL lookup. Threaded through to
   *  ModelPickerTable; keyed on no-llm type so per-tenant renames
   *  don't break the fallback. */
  providerLogoByType?: Record<string, string>;
  /** Called when a preset is selected. Passes the handle and resolved models. */
  onPresetApply?: (handle: string, models: IModelExtended[]) => void;
}

const searchFiltersInitialState = {
  provider: [],
  host: [],
  normalizedSpeed: [],
  quality: [],
  location: [],
  capabilities: [],
};

const getDefaultCustomValue = (value: string[]) => {
  return value.reduce((acc: Record<string, boolean>, item) => {
    acc[item] = true;
    return acc;
  }, {});
};

const checkIfInRange = (value: string | number, rangeString: string) => {
  const range = rangeString.split("-");
  const numValue = Number(value);
  const min = Number(range[0]);
  const max = Number(range[1]);

  if (
    typeof numValue !== "number" &&
    typeof min !== "number" &&
    typeof max !== "number"
  ) {
    return false;
  }

  return numValue >= min && numValue <= max;
};

export function ModelModal({
  isOpen,
  onClose,
  models,
  modelType,
  selectedModels,
  setSelectedModels,
  customValue,
  minModels = 0,
  maxModels,
  layout = "stacked",
  allModelsData,
  showPresets = true,
  hideDisabledModels = false,
  providerLogoByType,
  onPresetApply,
}: IModelModal) {
  /* Manage row state */
  const [sorting, setSorting] = useState<SortingState>([]);
  const [presetResetKey, setPresetResetKey] = useState(0);

  // Wraps setSelectedModels for manual model changes (not preset selection),
  // so the PresetSelector resets to "Presets" when models are customized.
  const handleModelsChange: Dispatch<SetStateAction<IModelExtended[]>> =
    useCallback(
      (models) => {
        setSelectedModels(models);
        setPresetResetKey((k) => k + 1);
      },
      [setSelectedModels]
    );

  /* Filter variables */
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchFilters, setSearchFilters] = useState<Record<string, string[]>>(
    searchFiltersInitialState
  );

  /* Filter stuff */
  const hasFiltersApplied = useMemo(() => {
    return Object.values(searchFilters).some(
      (arrayOfValues: string[]) => arrayOfValues.length > 0
    );
  }, [searchFilters]);

  const resetFilters = useCallback(() => {
    setSearchFilters({
      ...searchFiltersInitialState,
    });
  }, []);

  /* The list of models that are not selected and fulfill the selected filters/search */
  const filteredModels = useMemo(() => {
    // Search what the table actually shows (``title``) as well as the id.
    // The two are near-identical for built-ins ("gpt-4o-mini" / "GPT 4o Mini")
    // which hid this for a long time, but they diverge completely for custom
    // models: the id is derived from the API slug while the title is whatever
    // the user typed, so searching the visible name matched nothing.
    let modelsArray = filterObjBasedOnSearch(models, searchValue, [
      "title",
      "name",
    ]);

    // Filter by per-link ``id`` (qualified slug) so multi-instance
    // tenants with two "GPT 4o Mini" rows (one per OpenAI account)
    // only hide the variant the user actually picked. ``name`` is the
    // bare model id and would drop both rows.
    // Also exclude disabled/disconnected models from the catalog —
    // they should only appear in the chosen-models list (with
    // broken-link icon) if already selected, not be pickable.
    const selectedIds = new Set(selectedModels.map((model) => model.id));
    modelsArray = modelsArray.filter(
      (m) =>
        !selectedIds.has(m.id) &&
        m.is_active !== false &&
        m.provider_is_active !== false &&
        (!hasFiltersApplied ||
          Object.keys(searchFilters).every((key) => {
            if (searchFilters[key].length === 0) {
              return true;
            }

            if (key === "normalizedSpeed" || key === "quality") {
              return searchFilters[key].some((range) =>
                checkIfInRange(m[key], range)
              );
            } else if (key === "capabilities") {
              const modelCaps = m.capabilities ?? [];
              return searchFilters[key].every((cap) =>
                modelCaps.includes(cap as never)
              );
            } else {
              return searchFilters[key].includes(
                m[key as keyof IModelExtended] as string
              );
            }
          }))
    );

    if (sorting && sorting.length > 0) {
      modelsArray = sortByProperty(
        modelsArray,
        sorting[0].id,
        sorting[0].desc
      ) as IModelExtended[];
    }

    return modelsArray;
  }, [
    models,
    searchValue,
    sorting,
    searchFilters,
    hasFiltersApplied,
    selectedModels,
  ]);

  /* Handlers */
  const handleClose = () => {
    onClose();
  };

  const isSideBySide = layout === "side_by_side";
  const isCompact = isSideBySide;
  const hasNoScores =
    modelType === "embeddings" ||
    modelType === "decision_models" ||
    modelType === "voice_models";
  const presetModelsData = allModelsData ?? models;

  const hasActiveSearch = hasFiltersApplied || searchValue.length > 0;

  const explorerEmptyState = (
    <div className="h-full flex flex-1 items-center justify-center">
      <NoxusEmptyBlock
        icon={hasActiveSearch ? SearchX : Sparkles}
        title={
          hasActiveSearch
            ? "No models match your search"
            : "No models available"
        }
        description={
          hasActiveSearch
            ? "Try adjusting your filters or search terms."
            : "There are no models available to select."
        }
        size="sm"
        {...(hasActiveSearch && {
          button: "Clear filters",
          buttonIcon: X,
          buttonVariant: "outline",
          onClick: () => {
            setSearchValue("");
            resetFilters();
          },
        })}
      />
    </div>
  );

  const selectedEmptyState = (
    <NoxusEmptyBlock
      icon={MousePointerClick}
      title="No models selected"
      description={
        isSideBySide
          ? "Pick models from the left panel to add them here."
          : "Select models from the explorer below."
      }
      size="sm"
    />
  );

  const displaySelectedModels = selectedModels;

  const warningAlert =
    !hideDisabledModels &&
    selectedModels.some(
      (model) => model.is_active === false || model.provider_is_active === false
    ) ? (
      <NoxusAlert
        className="mb-4"
        variant="warning"
        size="xs"
        title="Models not available"
        description="Some selected models are disabled or not configured. You can enable them on the Models page in Settings."
        action={{
          label: (
            <>
              <ExternalLink size={12} />
              Go to Models
            </>
          ),
          onClick: () =>
            window.open("/settings/platform/models", "_blank", "noopener"),
        }}
      />
    ) : null;

  return (
    <BaseDialog
      title={"Your model selection"}
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      size="6xl"
    >
      {isSideBySide ? (
        <div className="relative flex flex-col h-144">
          {warningAlert}
          <div className="flex-1 min-h-0 flex gap-6">
            {/* Left panel: unselected / explorer */}
            <div
              className="flex-1 flex flex-col min-w-0 border rounded-lg overflow-hidden"
              data-testid="model-explorer"
            >
              <div className="p-4 border-b bg-background2 flex flex-col gap-4">
                <div className="flex gap-4 items-center">
                  <div className="flex-1">
                    <SearchBar
                      value={searchValue}
                      placeholder="Search models..."
                      setValue={setSearchValue}
                    />
                  </div>
                  <ModelPickerFilters
                    models={models || []}
                    searchFilters={searchFilters}
                    setSearchFilters={setSearchFilters}
                    resetFilters={resetFilters}
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <ModelPickerTable
                  data={filteredModels}
                  modelType={modelType}
                  setSelectedData={handleModelsChange}
                  sorting={sorting}
                  setSorting={setSorting}
                  isSortable
                  cantSelectMore={
                    maxModels != null &&
                    displaySelectedModels.length >= maxModels
                  }
                  emptyState={explorerEmptyState}
                  maxModels={maxModels}
                  isReducedColumns={hasNoScores}
                  isCompact={isCompact}
                  providerLogoByType={providerLogoByType}
                  className="border-0 rounded-none h-full"
                />
              </div>
            </div>

            {/* Right panel: selected */}
            <div
              className="flex-1 flex flex-col min-w-0 border rounded-lg overflow-hidden"
              data-testid="model-selection"
            >
              <div className="p-4 border-b bg-background2 flex flex-col gap-4">
                <div className="flex items-center justify-between h-8 gap-2">
                  <p className="text-sm font-medium">Selected models</p>
                  <div className="flex items-center gap-2">
                    {maxModels != null && (
                      <Badge variant="outline">
                        {displaySelectedModels.length} / {maxModels}
                      </Badge>
                    )}
                  </div>

                  {displaySelectedModels.length > 0 && (
                    <Button
                      variant="ghost"
                      className="text-xs h-8 ml-auto"
                      onClick={() => handleModelsChange([])}
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <ModelPickerTable
                  data={displaySelectedModels}
                  setSelectedData={handleModelsChange}
                  modelType={modelType}
                  sorting={sorting}
                  setSorting={setSorting}
                  emptyState={selectedEmptyState}
                  maxModels={maxModels}
                  isReducedColumns={hasNoScores}
                  isCompact={isCompact}
                  providerLogoByType={providerLogoByType}
                  className="border-0  rounded-none h-full"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "relative flex flex-col",
            maxModels === 1 ? "" : "h-160"
          )}
        >
          {warningAlert}

          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2 shrink-0">
              <p className="text-sm font-medium">Picked models</p>
              {showPresets && (
                <PresetSelector
                  key={presetResetKey}
                  allModelsUpdatedData={presetModelsData}
                  onSelectPreset={setSelectedModels}
                  onPresetApply={onPresetApply}
                />
              )}
            </div>

            <div className="flex-1 min-h-0">
              <ModelPickerTable
                data={displaySelectedModels}
                setSelectedData={handleModelsChange}
                modelType={modelType}
                sorting={sorting}
                setSorting={setSorting}
                emptyState={selectedEmptyState}
                maxModels={maxModels}
                isReducedColumns={hasNoScores}
                className="h-full"
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-end justify-between gap-2 mt-2 mb-2 shrink-0">
              <div className="flex items-center gap-2">
                <p className="text-sm flex-1 font-medium">Model explorer</p>

                <Link
                  href="https://artificialanalysis.ai/models"
                  rel="noopener noreferrer"
                  target="_blank"
                  className="flex"
                >
                  <Button variant="ghost" size="iconsm">
                    <ExternalLink className="size-4" strokeWidth={1.25} />
                  </Button>
                </Link>
              </div>

              <div className="flex gap-2 items-center grow-0 shrink-0">
                {hasFiltersApplied && (
                  <Button variant="outline" onClick={resetFilters}>
                    Clear
                  </Button>
                )}
                <ModelPickerFilters
                  models={models || []}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
                  resetFilters={resetFilters}
                />
                <div className="w-60">
                  <SearchBar
                    value={searchValue}
                    placeholder="Model name"
                    setValue={setSearchValue}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <ModelPickerTable
                data={filteredModels}
                modelType={modelType}
                setSelectedData={handleModelsChange}
                sorting={sorting}
                setSorting={setSorting}
                isSortable
                cantSelectMore={
                  maxModels != null && selectedModels.length >= maxModels
                }
                emptyState={explorerEmptyState}
                maxModels={maxModels}
                isReducedColumns={hasNoScores}
                className="h-full"
              />
            </div>
          </div>
        </div>
      )}
    </BaseDialog>
  );
}
